import { Link } from '@tanstack/react-router';
import type { CleaningOperationalState, CleaningRangeSummaryResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';

const statePresentation: Record<CleaningOperationalState, { label: string; dot: string }> = {
  Cancelled: { label: 'Entfällt', dot: 'bg-ink-faint' },
  Issue: { label: 'Mit Meldung', dot: 'bg-sealwax' },
  Ready: { label: 'Erledigt', dot: 'bg-forest' },
  InProgress: { label: 'Läuft', dot: 'bg-amber' },
  Pending: { label: 'Offen', dot: 'bg-ink-soft' },
};

function formatWindow(task: CleaningRangeSummaryResponse): string {
  const start = task.scheduledFromLocalTime.slice(0, 5);
  return task.scheduledToLocalTime
    ? `${start}–${task.scheduledToLocalTime.slice(0, 5)}`
    : `ab ${start}`;
}

/** Eine Reinigung in der Woche: Fenster, Person, Fotos; Auffälliges als roter Satz und Rand. */
export function CleaningOperationalTaskChip({
  task,
  selected = false,
  onSelect,
}: {
  task: CleaningRangeSummaryResponse;
  selected?: boolean;
  onSelect?: (task: CleaningRangeSummaryResponse, trigger: HTMLElement) => void;
}) {
  const presentation = statePresentation[task.state];
  const warnings = [
    task.isOverdue ? 'Überfällig' : null,
    task.isSameDayTurnover ? 'Anreise heute' : null,
    task.timeZoneDataQualityCode ? 'Zeitzone prüfen' : null,
  ].filter(Boolean);
  const photos =
    task.photoSlotCount > 0
      ? `${task.filledPhotoSlotCount} von ${task.photoSlotCount} Fotos`
      : 'ohne Fotos';
  const title = [
    presentation.label,
    formatWindow(task),
    task.isUnassigned ? 'Nicht zugewiesen' : task.assignedToUserName,
    photos,
    ...warnings,
  ]
    .filter(Boolean)
    .join(' · ');
  const content = (
    <>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className={cn('size-2 shrink-0 rounded-full', presentation.dot)} />
        <span className="text-ink truncate font-mono text-xs font-semibold tabular-nums">
          {formatWindow(task)}
        </span>
      </span>
      <span className="text-ink-soft mt-0.5 block truncate text-[11px]">
        {task.isUnassigned ? 'Nicht zugewiesen' : (task.assignedToUserName ?? 'Zuweisung prüfen')}
      </span>
      <span className="text-ink-soft block text-[11px] tabular-nums">{photos}</span>
      {warnings.length > 0 && (
        <span className="text-sealwax mt-0.5 block text-[11px] font-bold">
          {warnings.join(' · ')}
        </span>
      )}
    </>
  );
  const className = cn(
    'rounded-control block min-h-11 w-full border bg-white px-2 py-1.5 text-left transition-colors hover:border-forest',
    warnings.length > 0 ? 'border-sealwax/50' : 'border-hairline',
    selected && 'border-forest ring-forest ring-2',
  );
  return onSelect ? (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={title}
      onClick={(event) => onSelect(task, event.currentTarget)}
      className={className}
    >
      {content}
    </button>
  ) : (
    <Link to="/cleaning/$id" params={{ id: task.id }} aria-label={title} className={className}>
      {content}
    </Link>
  );
}
